// Firebase init for the customer-facing site. The service worker at
// public/firebase-messaging-sw.js handles BACKGROUND pushes; this file
// handles FOREGROUND pushes (page open) + token retrieval.
//
// All env vars are PUBLIC — Firebase clients are designed to ship them.
// The matching service worker carries the same config (inlined).

import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getMessaging,
  isSupported,
  getToken as fbGetToken,
  onMessage as fbOnMessage,
} from 'firebase/messaging';
import { getDatabase } from 'firebase/database';

const firebaseConfig = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  // databaseURL is required for the Realtime Database used by the
  // booking Track page's live-location stream. For us-central1 default
  // RTDB the URL shape is https://<project-id>-default-rtdb.firebaseio.com.
  databaseURL:       process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
const SW_URL = `${process.env.PUBLIC_URL || ''}/firebase-messaging-sw.js`;

const isConfigured = !!(firebaseConfig.apiKey && firebaseConfig.projectId && VAPID_KEY);

// getApps() prevents "Firebase app already initialised" during HMR.
const app = isConfigured
  ? (getApps().length ? getApp() : initializeApp(firebaseConfig))
  : null;

// Cache the messaging instance — `await isSupported()` is async.
let _messaging = null;
let _supportedChecked = false;
let _supportedResult = false;

const getMessagingSafe = async () => {
  if (!isConfigured) return null;
  if (_messaging) return _messaging;
  if (!_supportedChecked) {
    try {
      _supportedResult = await isSupported();
    } catch {
      _supportedResult = false;
    }
    _supportedChecked = true;
  }
  if (!_supportedResult) return null;
  _messaging = getMessaging(app);
  return _messaging;
};

// Returns the FCM token string, or null if:
//   • Firebase isn't configured
//   • Browser doesn't support FCM (Safari iOS <16.4, in-app browsers, etc.)
//   • User denied notification permission
//
// Caller is responsible for posting the token to the backend.
export const requestFcmToken = async () => {
  const messaging = await getMessagingSafe();
  if (!messaging) return null;

  // Permission must be granted before getToken can run. We don't auto-prompt
  // here — the calling hook decides when to ask.
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return null;
  }

  // Service worker registration — explicit so we control caching/scope.
  let swRegistration;
  try {
    swRegistration = await navigator.serviceWorker.register(SW_URL);
  } catch (err) {
    console.warn('[fcm] service worker registration failed:', err);
    return null;
  }

  try {
    return await fbGetToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swRegistration,
    });
  } catch (err) {
    console.warn('[fcm] getToken failed:', err);
    return null;
  }
};

// Subscribe to foreground messages. Returns an unsubscribe function.
//   onForegroundMessage(({ type, title, body, url, data }) => { … })
export const onForegroundMessage = (handler) => {
  let unsubscribe = () => {};
  getMessagingSafe().then((messaging) => {
    if (!messaging) return;
    unsubscribe = fbOnMessage(messaging, (payload) => {
      const d = payload?.data || {};
      let parsedMeta = null;
      if (d.meta) {
        try { parsedMeta = JSON.parse(d.meta); } catch { /* ignore */ }
      }
      handler({
        type:  d.type  || 'info',
        title: d.title || '',
        body:  d.body  || '',
        url:   d.url   || '',
        icon:  d.icon  || '',
        data:  parsedMeta,
        receivedAt: Date.now(),
      });
    });
  });
  return () => unsubscribe();
};

// Wraps the browser's permission API + Notification gate in a single helper.
// Returns 'granted' | 'denied' | 'default' | 'unsupported'.
export const requestPermission = async () => {
  if (typeof Notification === 'undefined') return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  try {
    return await Notification.requestPermission();
  } catch {
    return 'default';
  }
};

// True if the host browser can do FCM web push at all.
export const isFcmSupported = async () => {
  if (!isConfigured) return false;
  try { return await isSupported(); } catch { return false; }
};

export const isFirebaseConfigured = () => isConfigured;

// Realtime Database handle for the booking Track page's live-location
// listener. Returns null if the project isn't configured or no
// databaseURL was supplied — the Track page should keep working with
// the seeded last-known snapshot rather than crash.
export const getLocationDb = () => {
  if (!app || !firebaseConfig.databaseURL) return null;
  return getDatabase(app);
};
